import React, { useRef, useState } from 'react';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import api from '../wrapper/API';
import { motion } from 'framer-motion';
import { usePopup } from '../context/PopupContext';
import Popup from '../popup/Popup';
import logo from '../../images/favicon.ico';
import Textfield from '../input/Textfield';
import { validatePasswordChange, PASSWORD_REQUIREMENTS_TEXT } from '../../util/passwordPolicy';
import { ROUTES } from '../../util/routes';
import SubmitButton from '../input/SubmitButton';
import { drain as drainOfflineQueue } from '../../offline/syncEngine';
import { leaveOffline } from '../../offline/connectivity';

// Ein Login-Versuch ist ein bewusster Schritt zurück ins Netz: er umgeht den Offline-Modus-Guard in API.js
// (sonst würde jeder Versuch bis zum Reload sofort abgelehnt) und beendet den Modus bei Erfolg.
const LOGIN_REQUEST_CONFIG = { offlineProbe: true };

function LoginForm() {
  const navigate = useNavigate();

  const emailTextFieldRef = useRef();
  const passwortTextFieldRef = useRef();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'twoFactor'
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const handleLoginSuccess = (data, passwordForChange) => {
    localStorage.setItem('playerId', data.id);
    localStorage.setItem('statusId', data.statusId);
    // Login bestätigt die Verbindung -> Offline-Modus beenden und Check-ins übertragen, die offline erfasst
    // wurden oder an der abgelaufenen Sitzung (401) hängen geblieben sind.
    leaveOffline();
    drainOfflineQueue();
    if (!data.hasChangedPassword) {
      setOldPassword(passwordForChange);
      openPopup('editinitialpassword', 0);
    } else {
      navigate(ROUTES.HOME);
    }
  };

  const handleSubmit = (event) => {
    setLoading(true);
    event.preventDefault();
    const requestBody = {
      email: emailTextFieldRef.current.value,
      password: passwortTextFieldRef.current.value
    };

    api.post(`/Login/Login`, requestBody, LOGIN_REQUEST_CONFIG)
      .then((response) => {
        setLoading(false);
        setError(null);
        if (response.data.requiresTwoFactor) {
          setTempToken(response.data.tempToken);
          setStep('twoFactor');
        } else {
          handleLoginSuccess(response.data, requestBody.password);
        }
      })
      .catch(error => {
        setLoading(false);
        setError(error.response?.data ?? 'Server nicht erreichbar.');
      });
  };

  const handleVerifyTwoFactor = (event) => {
    setLoading(true);
    event.preventDefault();
    api.post('/Login/VerifyTwoFactor', {
      tempToken,
      code: twoFactorCode.trim(),
      rememberDevice: true
    }, LOGIN_REQUEST_CONFIG)
      .then((response) => {
        setLoading(false);
        setError(null);
        handleLoginSuccess(response.data, password);
      })
      .catch(error => {
        setLoading(false);
        setError(error.response?.data ?? 'Code ungültig oder abgelaufen.');
        setTwoFactorCode('');
      });
  };

  //Edit Password
  const [passwordError, setPasswordError] = useState(undefined);
  const [oldPassword, setOldPassword] = useState('');
  const { openPopup, closePopup } = usePopup();
  const newPasswordInputRef = useRef();
  const newRepeatPasswordInputRef = useRef();
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();

  const handleChangePasswordConfirm = () => {
    const error = validatePasswordChange(newPasswordInputRef.current.value, newRepeatPasswordInputRef.current.value, oldPassword);
    if (error) { setPasswordError(error); return; }

    const requestBody = {
      oldPassword: oldPassword,
      newPassword: newPasswordInputRef.current.value
    };

    api.put(`/Login/ChangePassword`, requestBody)
      .then(() => {
        setPasswordError(undefined);
        closePopup();
        navigate(ROUTES.HOME);
      })
      .catch(error => {
        setPasswordError(error.response.data);
      });
  };

  const handleForgotPasswordClick = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  const loadingAnimation = (
    <div className="submitLoadingContainer">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="submitLoadingIcon"
          animate={{ scale: [1, 1.8, 1] }}
          transition={{
            delay: i * 0.2,
            duration: 0.6,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatDelay: 0.5
          }}
        />
      ))}
    </div>
  );

  return (
    <React.Fragment>
      <div className={'loginContainer'}>
        <div className={'loginFormContainer'}>
          <img className={'logo'} src={logo} alt="" />
          {step === 'twoFactor' ? (
            <form onSubmit={handleVerifyTwoFactor}>
              <p className={'loginLabel'}>Code eingeben</p>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
                Wir haben dir einen 6-stelligen Code per E-Mail gesendet.
              </p>
              <div className={'textFieldGeneralContainer'}>
                <Textfield
                  icon={'pin'}
                  type={'text'}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required={true}
                  label={'Bestätigungscode'}
                  placeholder={'123456'}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  autoFocus
                />
              </div>
              {error !== null && <p className={'loginError'}>{error}</p>}
              <div className={'submitContainer'}>
                {loading ? loadingAnimation : <SubmitButton icon={'verified_user'} label={'Bestätigen'} />}
              </div>
              <div className={'backLink'} onClick={() => { setStep('credentials'); setError(null); }}>
                Zurück
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className={'loginLabel'}>Login</p>
              <div className={'textFieldGeneralContainer'}>
                <Textfield icon={'alternate_email'} ref={emailTextFieldRef} type={'email'} required={true} name={'email'} label={'E-Mail'} placeholder={'max.mustermann@test.de'} onChange={(e) => setEmail(e.target.value)} value={email} />
                <Textfield icon={'vpn_key'} ref={passwortTextFieldRef} type={'password'} required={true} name={'password'} label={'Passwort'} placeholder={'Passwort eingeben'} onChange={(e) => setPassword(e.target.value)} value={password} />
              </div>
              {error !== null && <p className={'loginError'}>{error}</p>}
              <div className={'forgotPassword'} onClick={handleForgotPasswordClick}>Passwort vergessen?</div>
              <div className={'submitContainer'}>
                {loading ? loadingAnimation : <SubmitButton icon={'login'} label={'Login'} />}
              </div>
              <div className={'signUpLink'}>
                Noch kein Account?<a href='mailto:info@av-enztal.de?subject=Account%20Anfrage&body=Hallo,%0Aich%20melde%20mich%20wegen%20einer%20Account%20Anfrage%20in%20eurem%20Orga%20System.%20Hier%20sind%20die%20benötigten%20Daten:%0A-%20Vorname:%20[Hier%20den%20Vornamen%20eintragen]%0A-%20Nachname:%20[Hier%20den%20Nachnamen%20eintragen]%0A-%Telefonnummer:%20[Hier%20die%20Telefonnummer%20eintragen]%0A'>
                  Kontaktiere uns
                </a>
              </div>
            </form>
          )}
          <div className={'privacyLink'}>
            <a href='https://av-enztal.de/datenschutz' target='_blank' rel='noopener noreferrer'>Datenschutzerklärung</a>
          </div>
        </div>
      </div>
      <Popup name='editinitialpassword' confirmText={'Übernehmen'} onConfirm={handleChangePasswordConfirm} dontCloseOnConfirm type="info">
        <h3>Bitte ändere dein Passwort;</h3>
        <input required type='password' ref={newPasswordInputRef} className={'textInput'} placeholder='Neues Passwort' />
        <input required type='password' ref={newRepeatPasswordInputRef} className={'textInput'} placeholder='Neues Passwort wiederholen' />
        <p className={'passwordHint'}>{PASSWORD_REQUIREMENTS_TEXT}</p>
        {passwordError &&
          <p className={'newError'}>{passwordError}</p>
        }
      </Popup>
    </React.Fragment>
  );
}

export default LoginForm;
